/**
 * usePresenceTracking - Real-time presence tracking for user journey funnel
 * 
 * Usage:
 *   usePresenceTracking('landing');     // On landing page
 *   usePresenceTracking('signing_in');  // On login form
 *   usePresenceTracking('signing_up');  // On signup form
 *   usePresenceTracking('onboarding');  // During onboarding
 *   usePresenceTracking('journaling');  // Active in journal
 */

import { useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const HEARTBEAT_INTERVAL = 30000; // 30 seconds

// Generate or retrieve persistent session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('presence_session_id');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    sessionStorage.setItem('presence_session_id', sessionId);
  }
  return sessionId;
};

// Send heartbeat to server
const sendHeartbeat = async (stage, userId = null) => {
  try {
    const sessionId = getSessionId();
    const pagePath = window.location.pathname;
    
    console.log('[Presence] Sending heartbeat:', { stage, sessionId, pagePath });
    
    const response = await fetch(`${API_URL}/api/tracking/presence`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: sessionId,
        stage,
        page_path: pagePath,
        user_id: userId
      })
    });
    
    if (response.ok) {
      console.log('[Presence] Heartbeat sent successfully');
    }
  } catch (error) {
    // Silently fail - presence tracking should not disrupt user experience
    console.debug('[Presence] Heartbeat failed:', error.message);
  }
};

/**
 * Hook for tracking user presence at a specific stage
 * @param {string} stage - The funnel stage ('landing', 'signing_in', 'signing_up', 'onboarding', 'journaling')
 * @param {string|null} userId - Optional user ID if authenticated
 */
export const usePresenceTracking = (stage, userId = null) => {
  const intervalRef = useRef(null);
  
  useEffect(() => {
    // Send initial heartbeat immediately
    sendHeartbeat(stage, userId);
    
    // Set up interval for continuous heartbeats
    intervalRef.current = setInterval(() => {
      sendHeartbeat(stage, userId);
    }, HEARTBEAT_INTERVAL);
    
    // Cleanup on unmount or stage change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [stage, userId]);
};

export default usePresenceTracking;
